import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { ModalDirective } from 'angular-bootstrap-md';
import { Transaction } from 'src/app/shared/models/transactions/transaction';
import { TransactionService } from 'src/app/services/transaction.service';
import { TransactionUserFiaPins } from 'src/app/shared/models/transactions/transaction-user-fia-pins';
import { TransactionFiaSdaEditData } from 'src/app/shared/models/transactions/transaction-fia-sda-edit-data';
import {
  UpdateTransactionSdaPinRequest,
  TransactionPinAllocation,
} from 'src/app/shared/models/transactions/update-transaction-sda-pin-request';
import { NgForm } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

interface EditablePinRow {
  userFiaPinId: number;
  pin: string;
  pinBalance: number;
  amountUsed: number;
}

@Component({
  selector: 'app-transaction-fia-sda-edit',
  templateUrl: './transaction-fia-sda-edit.component.html',
  styleUrls: ['./transaction-fia-sda-edit.component.scss'],
})
export class TransactionFiaSdaEditComponent implements OnInit, OnChanges {
  @ViewChild('fiaSdaModal', { static: true }) fiaSdaModal: ModalDirective;

  @Input() showModal: boolean = false;
  @Input() transaction: Transaction;

  @Output() showModalChange = new EventEmitter<boolean>();
  @Output() onModelChange = new EventEmitter<Transaction>();
  @Output() onError = new EventEmitter<HttpErrorResponse>();

  busy: boolean = false;
  busyText: string = '';
  loadingData: boolean = false;

  editData: TransactionFiaSdaEditData;
  editablePins: EditablePinRow[] = [];
  editableSdaAmount: number = 0;
  remainingSdaAvailable: number = 0;

  totalTradeAmount: number = 0;
  totalFiaAllocated: number = 0;
  totalAllocated: number = 0;
  remainingToAllocate: number = 0;

  validationError: string = '';
  showValidationWarning: boolean = false;

  saveError: string = '';
  showSaveError: boolean = false;

  constructor(private transactionService: TransactionService) {}

  ngOnInit(): void {}

  ngOnChanges(): void {
    if (this.showModal && this.transaction) {
      this.loadEditData();
    }
    this.showHideModal(this.showModal);
  }

  loadEditData() {
    this.loadingData = true;
    this.busyText = 'Loading FIA Pins and SDA data...';

    this.transactionService.GetTransactionFiaSdaEditData(this.transaction.id).subscribe({
      next: (data: TransactionFiaSdaEditData) => {
        this.editData = data;
        this.totalTradeAmount = data.totalTradeAmount;
        this.remainingSdaAvailable = data.remainingSdaAvailable;

        // Initialize editable SDA amount from transaction
        this.editableSdaAmount = this.transaction.sdaAmount || 0;

        // Build editable pin rows
        this.buildEditablePins();

        // Calculate initial totals
        this.updateTotals();

        this.loadingData = false;
        this.busyText = '';
      },
      error: (error) => {
        this.loadingData = false;
        this.busyText = '';
        this.showSaveError = true;
        this.saveError = error?.error?.ErrorDetail || error?.message || 'An error occurred while loading data. Please try again.';
      },
    });
  }

  buildEditablePins() {
    this.editablePins = [];

    // Create a map of currently assigned amounts
    const assignedAmounts = new Map<number, number>();
    if (this.editData.currentlyAssignedPins) {
      this.editData.currentlyAssignedPins.forEach((assignedPin) => {
        assignedAmounts.set(assignedPin.userFiaPinId, assignedPin.amountUsed);
      });
    }

    // Build rows from available pins (API already filters out expired and zero balance)
    if (this.editData.availableUserPins) {
      this.editData.availableUserPins.forEach((pin) => {
        const amountUsed = assignedAmounts.get(pin.id) || 0;
        this.editablePins.push({
          userFiaPinId: pin.id,
          pin: pin.pin,
          pinBalance: pin.pinBalance,
          amountUsed: amountUsed,
        });
      });
    }
  }

  updateTotals() {
    // Calculate total FIA from pins
    this.totalFiaAllocated = this.editablePins.reduce((sum, pin) => sum + (pin.amountUsed || 0), 0);

    // Calculate total allocated (FIA + SDA)
    this.totalAllocated = this.totalFiaAllocated + (this.editableSdaAmount || 0);

    // Calculate remaining to allocate
    this.remainingToAllocate = this.totalTradeAmount - this.totalAllocated;

    // Validate
    this.validate();
  }

  validate(): boolean {
    this.showValidationWarning = false;
    this.validationError = '';

    // Check if total matches trade amount
    if (Math.abs(this.remainingToAllocate) > 0.01) {
      this.showValidationWarning = true;
      if (this.remainingToAllocate > 0) {
        this.validationError = `Remaining R ${Math.round(this.remainingToAllocate).toLocaleString()} to allocate`;
      } else {
        this.validationError = `Over-allocated by R ${Math.round(Math.abs(this.remainingToAllocate)).toLocaleString()}`;
      }
      return false;
    }

    // Check for negative amounts
    if (this.editableSdaAmount < 0) {
      this.showValidationWarning = true;
      this.validationError = 'SDA amount cannot be negative';
      return false;
    }

    for (const pin of this.editablePins) {
      if (pin.amountUsed < 0) {
        this.showValidationWarning = true;
        this.validationError = `Pin ${pin.pin} amount cannot be negative`;
        return false;
      }

      if (pin.amountUsed > pin.pinBalance) {
        this.showValidationWarning = true;
        this.validationError = `Pin ${pin.pin} amount exceeds available balance`;
        return false;
      }
    }

    // Check SDA doesn't exceed available
    if (this.editableSdaAmount > this.remainingSdaAvailable) {
      this.showValidationWarning = true;
      this.validationError = 'SDA amount exceeds available balance';
      return false;
    }

    return true;
  }

  fillPin(pin: EditablePinRow) {
    if (this.remainingToAllocate <= 0) {
      return;
    }

    const amountToFill = Math.min(pin.pinBalance, this.remainingToAllocate);
    pin.amountUsed = amountToFill;
    this.updateTotals();
  }

  clearPin(pin: EditablePinRow) {
    pin.amountUsed = 0;
    this.updateTotals();
  }

  fillSda() {
    if (this.remainingToAllocate <= 0) {
      return;
    }

    const amountToFill = Math.min(this.remainingSdaAvailable, this.remainingToAllocate);
    this.editableSdaAmount = amountToFill;
    this.updateTotals();
  }

  clearSda() {
    this.editableSdaAmount = 0;
    this.updateTotals();
  }

  fillAll() {
    // Clear everything first
    this.editablePins.forEach((pin) => (pin.amountUsed = 0));
    this.editableSdaAmount = 0;

    let remaining = this.totalTradeAmount;

    // Fill pins first (all pins are valid since API filters expired ones)
    for (const pin of this.editablePins) {
      if (remaining <= 0) break;

      const amountToUse = Math.min(pin.pinBalance, remaining);
      pin.amountUsed = amountToUse;
      remaining -= amountToUse;
    }

    // Fill SDA if still remaining
    if (remaining > 0) {
      const sdaToUse = Math.min(this.remainingSdaAvailable, remaining);
      this.editableSdaAmount = sdaToUse;
      remaining -= sdaToUse;
    }

    this.updateTotals();
  }

  clearAll() {
    this.editablePins.forEach((pin) => (pin.amountUsed = 0));
    this.editableSdaAmount = 0;
    this.updateTotals();
  }

  saveClicked(form: NgForm | undefined) {
    // Validate form
    if (form?.invalid) {
      form.form.markAllAsTouched();
      return;
    }

    // Validate allocation
    if (!this.validate()) {
      return;
    }

    this.busy = true;
    this.busyText = 'Saving FIA/SDA allocation...';

    // Build the request model
    const request = new UpdateTransactionSdaPinRequest();
    request.sdaAmount = this.editableSdaAmount;

    for (const pin of this.editablePins) {
      if (pin.amountUsed > 0) {
        const allocation = new TransactionPinAllocation();
        allocation.userFiaPinId = pin.userFiaPinId;
        allocation.amountToAllocate = pin.amountUsed;
        request.pinAllocations.push(allocation);
      }
    }

    // Call API
    this.transactionService.UpdateSdaPinAllocation(this.transaction.id, request).subscribe({
      next: (result: Transaction) => {
        this.onModelChange.emit(result);
        this.busy = false;
        this.showHideModal(false);
      },
      error: (error) => {
        this.busy = false;
        this.showSaveError = true;
        this.saveError = error?.error?.ErrorDetail || error?.message || 'An error occurred while saving. Please try again.';
      },
    });
  }

  showHideModal(toggle: boolean) {
    if (toggle != null) {
      this.showModal = toggle;
    }

    // Reset error state when closing modal
    if (!toggle) {
      this.showSaveError = false;
      this.saveError = '';
    }

    if (this.showModal) {
      this.fiaSdaModal.show();
    } else {
      this.fiaSdaModal.hide();
    }

    this.showModalChange.emit(this.showModal);
  }

  trackByPinId(index: number, pin: EditablePinRow): number {
    return pin.userFiaPinId;
  }
}
